import { UserId as UserIdT, ProjectId as ProjectIdT, Team } from 'api/models';
import { Content, Empty, Loaded, Loading } from 'stores/core';
import { ProjectPermissionSetType } from 'stores/permission-sets';
import { Memberships } from './memberships';

type ProjectId = number;
type Timestamp = number;

export interface MembershipsStore {
  byProjectId: {
    [projectId: number]: Content<Memberships>;
  };
  projects: ProjectId[];
}

const EMPTY: MembershipsStore = {
  byProjectId: {},
  projects: [],
};

export interface MembershipData {
  userId: UserIdT;
  memberType: ProjectPermissionSetType;
  joinedAt?: Timestamp;
}

export const MembershipsStore = {
  empty: () => EMPTY,
  setLoading:
    ([, projectId]: ProjectIdT) =>
    ({ byProjectId, projects }: MembershipsStore) => ({
      projects: projects.filter((x) => x !== projectId).concat(projectId),
      byProjectId: {
        ...byProjectId,
        [projectId]: Loading(),
      },
    }),
  load:
    (
      [, projectId]: ProjectIdT,
      memberships: MembershipData[],
      placeholders?: Team[],
    ) =>
    ({ byProjectId, projects }: MembershipsStore) => ({
      projects: projects.filter((x) => x !== projectId).concat(projectId),
      byProjectId: {
        ...byProjectId,
        [projectId]: Loaded(
          memberships.reduce(
            (
              membership: Memberships,
              { userId, memberType, joinedAt }: MembershipData,
            ) => Memberships.set(userId, memberType, joinedAt)(membership),
            { ...Memberships.empty(), placeholders },
          ),
        ),
      },
    }),
  add:
    ([, projectId]: ProjectIdT, memberships: MembershipData[]) =>
    ({ byProjectId, projects }: MembershipsStore) => {
      const [, initial = Memberships.empty()] =
        byProjectId[projectId] ?? Empty();

      return {
        projects: projects.filter((x) => x !== projectId).concat(projectId),
        byProjectId: {
          ...byProjectId,
          [projectId]: Loaded(
            memberships.reduce(
              (
                membership: Memberships,
                { userId, memberType, joinedAt }: MembershipData,
              ) => Memberships.set(userId, memberType, joinedAt)(membership),
              initial,
            ),
          ),
        },
      };
    },
  remove:
    ([, projectId]: ProjectIdT, userIds: UserIdT[]) =>
    ({ byProjectId, projects }: MembershipsStore) => {
      const [, initial = Memberships.empty()] =
        byProjectId[projectId] ?? Empty();

      return {
        projects: projects.filter((x) => x !== projectId).concat(projectId),
        byProjectId: {
          ...byProjectId,
          [projectId]: Loaded(
            userIds.reduce(
              (membership: Memberships, userId: UserIdT) =>
                Memberships.reset(userId)(membership),
              initial,
            ),
          ),
        },
      };
    },
  ofProject:
    ([, projectId]: ProjectIdT) =>
    ({ byProjectId }: MembershipsStore) =>
      byProjectId[projectId] ?? Memberships.empty(),

  addProjectPlaceholder:
    ([, projectId]: ProjectIdT, team: Team) =>
    (state: MembershipsStore) => {
      const { byProjectId } = state;
      const [, initial = Memberships.empty()] =
        byProjectId[projectId] ?? Empty();

      return {
        ...state,
        byProjectId: {
          ...byProjectId,
          [projectId]: Loaded({
            ...initial,
            placeholders: [
              ...(initial.placeholders?.filter(
                ({ teamId }) => team.teamId !== teamId,
              ) ?? []),
              team,
            ],
          }),
        },
      };
    },

  updateProjectPlaceholder:
    ([, projectId]: ProjectIdT, team: Team) =>
    (state: MembershipsStore) => {
      const { byProjectId } = state;
      const [, initial = Memberships.empty()] =
        byProjectId[projectId] ?? Empty();

      return {
        ...state,
        byProjectId: {
          ...byProjectId,
          [projectId]: Loaded({
            ...initial,
            placeholders: [
              ...(initial.placeholders?.filter(
                ({ teamId }) => teamId !== team.teamId,
              ) ?? []),
              team,
            ],
          }),
        },
      };
    },

  removeProjectPlaceholder:
    ([, projectId]: ProjectIdT, teamId: number) =>
    (state: MembershipsStore) => {
      const { byProjectId } = state;
      const [, initial = Memberships.empty()] =
        byProjectId[projectId] ?? Empty();

      return {
        ...state,
        byProjectId: {
          ...byProjectId,
          [projectId]: Loaded({
            ...initial,
            placeholders:
              initial.placeholders?.filter((team) => team.teamId !== teamId) ??
              [],
          }),
        },
      };
    },
};
